import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { tracked } from '@glimmer/tracking';

import type { APIData, ImageCropData, PopupData, RequestError, Service } from 'webapp-types';
import ThemeConfig from 'webapp/const/theme-config';
import { TrackingEvent } from 'webapp/enums/tracking';
import { capitalize } from 'webapp/utils/common';

import CommonPopupGlobalVisualSettingsComponent from 'webapp/components/common/popup-global-visual-settings';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import FormColorPickerComponent from 'webapp/components/form/color-picker';
import FormLeaveConfirmPopupComponent from 'webapp/components/form/leave-confirm/popup';
import type { OptionRaw } from 'webapp/components/form/select';
import FormSelectComponent from 'webapp/components/form/select';
import FormSliderComponent from 'webapp/components/form/slider';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type Board from 'webapp/models-custom/board';
import ReportSlide from 'webapp/models-custom/report-slide';

const CustomColorId = 'custom';

export default class BoardPopupVisualSettingsComponent extends PopupInnerModalComponent<PopupData.Board.VisualSettings> {
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('request') declare requestService: Service.Request;
	@service('constants') declare constantsService: Service.Constants;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('popup') declare popupService: Service.Popup;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		// in case we havent saved yet and we rollback, so we dont lose datawall-specific attributes like type, color, ...
		next(() => {
			this.model.markAsClean();
		});

		if (this.isReportSlide) {
			this.applyVisualSettingsToAllSlides = !!(this.model as ReportSlide).report
				?.isVisualSettingsAppliedToAllSlides;
		}

		const color = this.model.color;
		if (this.availableColors.find((i) => i.value === color)) {
			this.selectedColor = color;
		} else {
			this.selectedColor = CustomColorId;
		}
	}

	get model() {
		return this.popupData.model;
	}

	get isReportSlide() {
		return this.model instanceof ReportSlide;
	}

	get isAdmin() {
		return this.currentUserService.isAdmin;
	}

	// ---- colors ----
	@tracked selectedColor = '';

	get availableColors() {
		const colors = this.constantsService.boardColors.map((colorData) => {
			return {
				value: colorData.hex,
				label: colorData.name,
				colordroplet: colorData.hex,
			};
		});
		colors.push({
			value: CustomColorId,
			label: 'Custom',
			colordroplet: 'stripes',
		});
		return colors;
	}

	get isCustomColorSelected() {
		return this.selectedColor === CustomColorId;
	}

	@action
	changeColor(color: string) {
		this.selectedColor = color;
		if (!this.isCustomColorSelected) {
			this.model.color = color;

			if (!this.availableThemeIds.includes(this.model.themeId)) {
				this.model.themeId = this.availableThemeIds[0]!;
			}
		}
	}

	@action
	changeCustomColor(color: string) {
		this.model.color = color;
	}

	// ---- themes ----
	get availableThemeIds() {
		return this.mmBoardsService.availableThemesForColor(this.selectedColor);
	}

	get availableThemes() {
		const themes: OptionRaw<number>[] = [];

		this.availableThemeIds.forEach((themeId) => {
			const theme = ThemeConfig.find((item) => item.id === themeId);

			if (theme) {
				themes.push({
					value: theme.id,
					label: theme.name,
					prefixClassName: `icon chart-theme-icon theme-${theme.id}`,
				});
			}
		});

		return themes;
	}

	@action
	changeTheme(themeId: number) {
		this.model.themeId = themeId;
	}

	// --- reports all slides setting ---
	@tracked applyVisualSettingsToAllSlides = false;

	get showApplyVisualSettingsToAllSlides() {
		return this.isReportSlide;
	}

	@action
	changeApplyVisualSettingsToAllSlides(value: boolean) {
		this.applyVisualSettingsToAllSlides = value;
	}

	// ---- bg & logo ----

	@action
	handleImage(type: 'bg' | 'logo') {
		const model = this.model,
			existingUrl = model[type];

		let originalUrl = '',
			crop: ImageCropData | null = null;

		if (existingUrl) {
			originalUrl = model[`${type}Original`] || existingUrl;
			crop = model[`${type}Crop`];
		}

		const onFinish: PopupData.Common.ImageUpload['onFinish'] = (url, originalUrl, crop) => {
			model[type] = url;
			model[`${type}Original`] = originalUrl;
			model[`${type}Crop`] = crop;
		};
		this.navigationService.openImageUploadPopup(onFinish, '', originalUrl, crop);
	}

	@tracked clearBgConfirm = false;
	@tracked clearLogoConfirm = false;

	get bgFileuploadBoxStyles() {
		let style = '';
		if (this.model.bg) {
			style = this.model.colorGradient;
		}
		return htmlSafe(style);
	}

	get logoFileuploadBoxStyles() {
		let style = '';
		if (this.model.logo) {
			style = this.model.colorGradient;
		}
		return htmlSafe(style);
	}

	get bgImageStyle() {
		return htmlSafe(`opacity: ${this.model.bgOpacity}`);
	}

	get bgOpacityPercent() {
		return this.model.bgOpacity * 100;
	}

	clearModelBg() {
		this.model.bg = '';
		this.model.bgOriginal = '';
		this.model.bgCrop = null;
		this.model.bgOpacity = this.mmBoardsService.defaultBgImageOpacity;
	}

	clearModelLogo() {
		this.model.logo = '';
		this.model.logoOriginal = '';
		this.model.logoCrop = null;
	}

	@action
	changeBgOpacityPercent(percent: number) {
		const bgOpacity = percent / 100;
		this.model.bgOpacity = bgOpacity;
	}

	@action
	clearBgStep1() {
		this.clearBgConfirm = true;
	}

	@action
	clearLogoStep1() {
		this.clearLogoConfirm = true;
	}

	@action
	clearBgStep2(confirmed: boolean) {
		if (confirmed) {
			this.clearModelBg();
		}
		this.clearBgConfirm = false;
	}

	@action
	clearLogoStep2(confirmed: boolean) {
		if (confirmed) {
			this.clearModelLogo();
		}
		this.clearLogoConfirm = false;
	}

	// ---- others ----
	@action
	checkIsDirty(): boolean {
		return this.model.isDirty;
	}

	@action
	save() {
		if (this.submitInProgress) {
			return;
		}

		this.submitInProgress = true;

		const isNew = this.model.isNew,
			changedProps = this.model.dirtyProperties,
			isReportSlide = this.isReportSlide,
			eventProps: APIData.Tracking.Data = {};

		// for tracking
		if (changedProps.includes('color')) {
			eventProps.colorChanged = true;
		}
		if (changedProps.includes('themeId')) {
			eventProps.themeChanged = true;
		}
		if (changedProps.includes('logo')) {
			if (this.model.logo) {
				eventProps.logoAdded = true;
			} else {
				eventProps.logoRemoved = true;
			}
		}
		if (changedProps.includes('bg')) {
			if (this.model.bg) {
				eventProps.bgAdded = true;
			} else {
				eventProps.bgRemoved = true;
			}
		}

		// for infolayer
		let layerType = 'dashboard';
		if (isReportSlide) {
			layerType = 'slide';
		} else if ((this.model as Board).isAccountTemplate) {
			layerType = 'template';
		}

		const layer = this.infolayerService.loading(`Updating ${layerType} settings...`);

		this.model
			.save()
			.then(
				() => {
					if (!isReportSlide && !(this.model as Board).isAccountTemplate) {
						this.analyticsService.event(
							TrackingEvent.DATABOARD_VISUAL_SETTINGS_MODIFIED,
							eventProps,
						);
					}

					layer.ok(`${capitalize(layerType)} settings updated!`);
					if (!isReportSlide && isNew) {
						// new board, mark it so we don't request 'data' again
						const boardId = this.model.id;
						if (boardId) {
							void this.mmBoardsService.markBoardRequestedAndSubscribeToDataChannel(
								boardId,
							);
							this.navigationService.replaceRoute(`/layout-designer/${boardId}`);
						}
					}

					if (isReportSlide && this.applyVisualSettingsToAllSlides) {
						const report = (this.model as ReportSlide).report;
						if (report) {
							this.mmReportsService.applyVisualSettingsToAllSlides(report, {
								color: this.model.color,
								themeId: this.model.themeId,
								bg: this.model.bg || '',
								bgOriginal: this.model.bgOriginal || '',
								bgCrop: this.model.bgCrop || null,
								bgOpacity: this.model.bgOpacity,
								logo: this.model.logo || '',
								logoOriginal: this.model.logoOriginal || '',
								logoCrop: this.model.logoCrop || null,
							});
						}
					}

					this.close();
				},
				(errorData: RequestError) => {
					layer.close();
					throw errorData;
				},
			)
			.finally(() => {
				this.submitInProgress = false;
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	@action
	openGlobalVisualSettingsPopup() {
		this.popupService
			.createModal(CommonPopupGlobalVisualSettingsComponent, {
				popupClass: 'popup-type-global-visual-settings',
			})
			.open({
				model: this.model,
				onSave: this.save,
			});
	}

	override willDestroy(): void {
		this.model?.rollback();
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="Brush" @size="lg" />
			Advanced visual settings
		</div>

		<div class="content">

			<div class="row">
				<label>Background color</label>
				<div class="right">
					<FormSelectComponent
						name="color"
						@options={{this.availableColors}}
						@selected={{this.selectedColor}}
						@isNumeric={{false}}
						@isRequired={{true}}
						@onChange={{this.changeColor}}
					/>
				</div>
				{{#if this.isCustomColorSelected}}
					<div class="customcolor">
						<FormColorPickerComponent
							@value={{this.model.color}}
							@colorIcon="raindrop"
							@onChange={{this.changeCustomColor}}
						/>
					</div>
				{{/if}}
			</div>

			{{#unless this.popupData.hideThemes}}
				<div class="row">
					<label>Chart theme</label>
					<div class="right">
						<FormSelectComponent
							name="theme"
							@options={{this.availableThemes}}
							@selected={{this.model.themeId}}
							@isRequired={{true}}
							@onChange={{this.changeTheme}}
						/>
					</div>
				</div>
			{{/unless}}

			<div id="wall-bg-ct" class="row">
				<label>Background image <small>(optional)</small></label>
				<div class="right">
					<a
						class="image-box {{unless this.model.bg 'empty'}}"
						{{on "click" (fn this.handleImage "bg")}}
					>
						{{#if this.model.bg}}
							<div class="bg" style={{this.bgFileuploadBoxStyles}}>
								<img src={{this.model.bg}} style={{this.bgImageStyle}} alt />
							</div>
						{{else}}
							<CommonUiIconComponent @name="Image" @size="2xl" />
						{{/if}}
					</a>
				</div>
				<p class="right">
					{{#if this.model.bg}}
						<div class="bg-opacity-slider">
							<label>Image opacity</label>
							<FormSliderComponent
								@onChange={{this.changeBgOpacityPercent}}
								@value={{this.bgOpacityPercent}}
								@min={{10}}
								@max={{100}}
								@step={{10}}
								@name="bgOpacity"
							/>
						</div>
						{{#if this.clearBgConfirm}}
							<a
								role="button"
								class="confirm-yes"
								{{on "click" (fn this.clearBgStep2 true)}}
							>
								<CommonUiIconComponent @name="Trash" @size="sm" />
								Confirm
							</a>
							<a
								role="button"
								class="confirm-no"
								{{on "click" (fn this.clearBgStep2 false)}}
							>
								Cancel
							</a>
						{{else}}
							<a role="button" {{on "click" this.clearBgStep1}}>
								<CommonUiIconComponent @name="Trash" @size="sm" />Delete
							</a>
						{{/if}}
					{{else}}
						Upload a background image, maximum 2000px wide, but not larger than 10 MB.
						JPG, PNG &amp; GIF allowed.
					{{/if}}
				</p>
			</div>

			<div id="wall-logo-ct" class="row">
				<label>Your company logo <small>(optional)</small></label>
				<div class="right">
					<a
						class="image-box {{unless this.model.logo 'empty'}}"
						{{on "click" (fn this.handleImage "logo")}}
					>
						{{#if this.model.logo}}
							<div class="logo" style={{this.logoFileuploadBoxStyles}}>
								<img src={{this.model.logo}} alt />
							</div>
						{{else}}
							<CommonUiIconComponent @name="Image" @size="2xl" />
						{{/if}}
					</a>
				</div>
				<p class="right">
					{{#if this.model.logo}}
						{{#if this.clearLogoConfirm}}
							<a
								role="button"
								class="confirm-yes"
								{{on "click" (fn this.clearLogoStep2 true)}}
							>
								<CommonUiIconComponent @name="Trash" @size="sm" />
								Confirm
							</a>
							<a
								role="button"
								class="confirm-no"
								{{on "click" (fn this.clearLogoStep2 false)}}
							>
								Cancel
							</a>
						{{else}}
							<a role="button" {{on "click" this.clearLogoStep1}}>
								<CommonUiIconComponent @name="Trash" @size="sm" />Delete
							</a>
						{{/if}}
					{{else}}
						Upload a logotype, at least 400px wide. It looks nicer with a transparent
						background. JPG, PNG &amp; GIF allowed.
					{{/if}}
				</p>
			</div>

			{{#if this.showApplyVisualSettingsToAllSlides}}
				<div class="row apply-checkbox">
					<label></label>
					<div class="right">
						<FormCheckboxComponent
							@name="apply_visual_settings_to_all_slides"
							@selected={{this.applyVisualSettingsToAllSlides}}
							@label="Apply to all slides"
							@onChange={{this.changeApplyVisualSettingsToAllSlides}}
						/>
					</div>
				</div>
			{{/if}}

			<div class="controls2">
				<div class="controls2__left">
					{{! "cancel" class is required for leave-confirm logic }}
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@extraClass="cancel"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
				</div>
				<div class="controls2__right">
					{{#if this.isAdmin}}
						<SubmitButtonComponent
							@text="Set as default"
							@submitInProgress={{this.submitInProgress}}
							@theme="blue-outlined"
							@onTrigger={{this.openGlobalVisualSettingsPopup}}
						/>
					{{/if}}

					<FormLeaveConfirmPopupComponent
						@popup={{@model}}
						@save={{this.save}}
						@checkIsDirty={{this.checkIsDirty}}
						as |controls|
					>
						<controls.save
							@text="Save"
							@extraClass="save"
							@submitInProgress={{this.submitInProgress}}
						/>
					</FormLeaveConfirmPopupComponent>
				</div>
			</div>
		</div>
	</template>
}
